import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowLeft, TrendingDown, TrendingUp, Minus, GitCompare,
  Shield, AlertTriangle, CheckCircle2, XCircle, ArrowRight,
  BarChart3, Target, Loader2, RefreshCw, ArrowDownRight, ArrowUpRight
} from 'lucide-react';
import { getScanTrends, getScanTargets, getScanDiff } from '@/services/litesabre';
import { toast } from 'sonner';

const SEV_COLORS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
  unknown: 'bg-gray-400 text-white',
};

const SEV_BAR_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-400',
  medium: 'bg-yellow-400',
  low: 'bg-blue-400',
};

const TrendChart = ({ data }) => {
  if (!data || data.length < 2) return null;
  const maxVal = Math.max(...data.map(d => d.total), 1);
  const chartH = 180;

  return (
    <div className="relative" data-testid="trend-chart">
      <div className="flex items-end gap-1 h-[180px]" style={{ minWidth: data.length * 56 }}>
        {data.map((d, i) => {
          const h = Math.max((d.total / maxVal) * chartH, 4);
          const crit = (d.critical / maxVal) * chartH;
          const high = (d.high / maxVal) * chartH;
          const med = (d.medium / maxVal) * chartH;
          const low = (d.low / maxVal) * chartH;
          return (
            <div key={d.scan_id} className="flex flex-col items-center flex-1 min-w-[48px]">
              <span className="text-[10px] text-muted-foreground mb-1 font-mono">{d.total}</span>
              <div className="w-8 flex flex-col-reverse rounded-t overflow-hidden" style={{ height: h }}>
                {d.low > 0 && <div className={SEV_BAR_COLORS.low} style={{ height: low }} />}
                {d.medium > 0 && <div className={SEV_BAR_COLORS.medium} style={{ height: med }} />}
                {d.high > 0 && <div className={SEV_BAR_COLORS.high} style={{ height: high }} />}
                {d.critical > 0 && <div className={SEV_BAR_COLORS.critical} style={{ height: crit }} />}
              </div>
              <span className="text-[9px] text-muted-foreground mt-1 truncate max-w-[52px]" title={d.date}>
                {d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `#${i+1}`}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Critical</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400" /> High</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-yellow-400" /> Medium</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400" /> Low</span>
      </div>
    </div>
  );
};

const DiffSummaryCard = ({ label, count, severity, icon: Icon, color }) => (
  <Card className={`border-l-4 ${color}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <p className="text-3xl font-bold">{count}</p>
      {severity && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {Object.entries(severity).filter(([,v]) => v > 0).map(([sev, c]) => (
            <Badge key={sev} className={`${SEV_COLORS[sev]} text-[10px] px-1.5 py-0`}>{sev[0].toUpperCase()}: {c}</Badge>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const FindingRow = ({ finding }) => (
  <TableRow data-testid={`finding-row-${finding.title?.slice(0,20)}`}>
    <TableCell>
      <Badge className={`${SEV_COLORS[finding.severity?.toLowerCase()] || SEV_COLORS.unknown} text-[10px]`}>
        {finding.severity?.toUpperCase()}
      </Badge>
    </TableCell>
    <TableCell className="font-medium text-sm max-w-[300px] truncate">{finding.title}</TableCell>
    <TableCell className="text-xs text-muted-foreground font-mono">{finding.package_name || '-'}</TableCell>
    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{finding.file_path || '-'}</TableCell>
    <TableCell className="text-xs font-mono">{finding.cve_id && finding.cve_id !== 'None' ? finding.cve_id : '-'}</TableCell>
  </TableRow>
);

const ScanTrendAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro')
    ? '/litesabre' : '/grc/litesabre';

  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(searchParams.get('target') || '');
  const [trendData, setTrendData] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [scanA, setScanA] = useState('');
  const [scanB, setScanB] = useState('');

  useEffect(() => { loadTargets(); }, []);

  useEffect(() => {
    if (selectedTarget) loadTrend(selectedTarget);
  }, [selectedTarget]);

  const loadTargets = async () => {
    try {
      setLoading(true);
      const data = await getScanTargets();
      setTargets(data.targets || []);
      if (!selectedTarget && data.targets?.length > 0) {
        setSelectedTarget(data.targets[0].target);
      }
    } catch { toast.error('Failed to load targets'); }
    finally { setLoading(false); }
  };

  const loadTrend = async (target) => {
    try {
      const data = await getScanTrends(target);
      setTrendData(data);
      // Auto-select last two scans for diff
      const t = data.trend || [];
      if (t.length >= 2) {
        setScanA(t[t.length - 2].scan_id);
        setScanB(t[t.length - 1].scan_id);
      }
      setDiffData(null);
    } catch { toast.error('Failed to load trend data'); }
  };

  const loadDiff = async () => {
    if (!scanA || !scanB || scanA === scanB) {
      toast.error('Select two different scans to compare');
      return;
    }
    try {
      setDiffLoading(true);
      const data = await getScanDiff(scanA, scanB);
      setDiffData(data);
    } catch { toast.error('Failed to load scan diff'); }
    finally { setDiffLoading(false); }
  };

  // Auto-load diff when scanA/scanB change
  useEffect(() => {
    if (scanA && scanB && scanA !== scanB) loadDiff();
  }, [scanA, scanB]);

  const trend = trendData?.trend || [];
  const latestDelta = useMemo(() => {
    if (trend.length < 2) return null;
    const prev = trend[trend.length - 2];
    const curr = trend[trend.length - 1];
    return {
      total: curr.total - prev.total,
      critical: curr.critical - prev.critical,
      high: curr.high - prev.high,
    };
  }, [trend]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="scan-trend-analysis">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/scans`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
              Scan Diff & Trend Analysis
            </h1>
            <p className="text-muted-foreground text-sm">Compare scans and track remediation progress over time</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadTargets}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Target Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Select Target</label>
              <Select value={selectedTarget} onValueChange={setSelectedTarget}>
                <SelectTrigger data-testid="target-selector">
                  <SelectValue placeholder="Choose a target with 2+ scans" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map(t => (
                    <SelectItem key={t.target} value={t.target}>
                      <div className="flex items-center gap-2">
                        <Target className="w-3 h-3" />
                        <span className="truncate max-w-[400px]">{t.target}</span>
                        <Badge variant="outline" className="text-[10px] ml-2">{t.scan_count} scans</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {targets.length === 0 && (
              <p className="text-sm text-muted-foreground">No targets with 2+ scans found. Run multiple scans on the same target to enable trend analysis.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {trendData && trend.length >= 2 && (
        <>
          {/* Trend Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Scans</p>
                <p className="text-3xl font-bold">{trend.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Latest Findings</p>
                <p className="text-3xl font-bold">{trend[trend.length - 1]?.total || 0}</p>
                {latestDelta && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${latestDelta.total < 0 ? 'text-green-600' : latestDelta.total > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {latestDelta.total < 0 ? <ArrowDownRight className="w-3 h-3" /> : latestDelta.total > 0 ? <ArrowUpRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    {Math.abs(latestDelta.total)} vs previous
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Critical+High (Latest)</p>
                <p className="text-3xl font-bold text-red-600">
                  {(trend[trend.length - 1]?.critical || 0) + (trend[trend.length - 1]?.high || 0)}
                </p>
                {latestDelta && (
                  <div className={`flex items-center gap-1 text-xs mt-1 ${(latestDelta.critical + latestDelta.high) < 0 ? 'text-green-600' : (latestDelta.critical + latestDelta.high) > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {(latestDelta.critical + latestDelta.high) < 0 ? <TrendingDown className="w-3 h-3" /> : (latestDelta.critical + latestDelta.high) > 0 ? <TrendingUp className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    {Math.abs(latestDelta.critical + latestDelta.high)} vs previous
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Overall Trend</p>
                <div className="flex items-center gap-2 mt-1">
                  {trend[trend.length - 1]?.total < trend[0]?.total ? (
                    <><TrendingDown className="w-6 h-6 text-green-600" /><span className="text-lg font-bold text-green-600">Improving</span></>
                  ) : trend[trend.length - 1]?.total > trend[0]?.total ? (
                    <><TrendingUp className="w-6 h-6 text-red-600" /><span className="text-lg font-bold text-red-600">Degrading</span></>
                  ) : (
                    <><Minus className="w-6 h-6 text-muted-foreground" /><span className="text-lg font-bold text-muted-foreground">Stable</span></>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-500" /> Vulnerability Trend
              </CardTitle>
              <CardDescription>Findings by severity across {trend.length} scans</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full">
                <TrendChart data={trend} />
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Scan Comparison Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-purple-500" /> Scan Comparison
              </CardTitle>
              <CardDescription>Select two scans to compare findings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs text-muted-foreground mb-1 block">Baseline (Older)</label>
                  <Select value={scanA} onValueChange={setScanA}>
                    <SelectTrigger data-testid="scan-a-selector"><SelectValue placeholder="Select baseline scan" /></SelectTrigger>
                    <SelectContent>
                      {trend.map(s => (
                        <SelectItem key={s.scan_id} value={s.scan_id}>
                          {s.name?.length > 40 ? s.name.slice(0,40) + '...' : s.name} — {s.total} findings
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground mt-5" />
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs text-muted-foreground mb-1 block">Current (Newer)</label>
                  <Select value={scanB} onValueChange={setScanB}>
                    <SelectTrigger data-testid="scan-b-selector"><SelectValue placeholder="Select current scan" /></SelectTrigger>
                    <SelectContent>
                      {trend.map(s => (
                        <SelectItem key={s.scan_id} value={s.scan_id}>
                          {s.name?.length > 40 ? s.name.slice(0,40) + '...' : s.name} — {s.total} findings
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadDiff} disabled={diffLoading || !scanA || !scanB || scanA === scanB} className="mt-5 bg-purple-600 hover:bg-purple-700" data-testid="compare-btn">
                  {diffLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitCompare className="w-4 h-4 mr-2" />}
                  Compare
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Diff Results */}
          {diffData && (
            <>
              {/* Diff Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <DiffSummaryCard label="Resolved" count={diffData.diff.resolved_count} severity={diffData.diff.resolved_severity} icon={CheckCircle2} color="border-l-green-500" />
                <DiffSummaryCard label="New" count={diffData.diff.new_count} severity={diffData.diff.new_severity} icon={XCircle} color="border-l-red-500" />
                <DiffSummaryCard label="Persistent" count={diffData.diff.persistent_count} severity={diffData.diff.persistent_severity} icon={AlertTriangle} color="border-l-yellow-500" />
                <Card className="border-l-4 border-l-indigo-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-muted-foreground">Remediation Rate</span>
                      <Shield className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-3xl font-bold">{diffData.remediation_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {diffData.scan_a.total} → {diffData.scan_b.total} findings
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Diff Detail Tabs */}
              <Tabs defaultValue="resolved">
                <TabsList className="grid grid-cols-3 w-full max-w-[500px]">
                  <TabsTrigger value="resolved" className="flex items-center gap-1" data-testid="resolved-tab">
                    <CheckCircle2 className="w-3 h-3 text-green-600" /> Resolved ({diffData.diff.resolved_count})
                  </TabsTrigger>
                  <TabsTrigger value="new" className="flex items-center gap-1" data-testid="new-tab">
                    <XCircle className="w-3 h-3 text-red-600" /> New ({diffData.diff.new_count})
                  </TabsTrigger>
                  <TabsTrigger value="persistent" className="flex items-center gap-1" data-testid="persistent-tab">
                    <AlertTriangle className="w-3 h-3 text-yellow-600" /> Persistent ({diffData.diff.persistent_count})
                  </TabsTrigger>
                </TabsList>

                {['resolved', 'new', 'persistent'].map(tab => {
                  const findings = tab === 'resolved' ? diffData.resolved : tab === 'new' ? diffData.new_findings : diffData.persistent;
                  return (
                    <TabsContent key={tab} value={tab}>
                      <Card>
                        <CardContent className="p-0">
                          {findings.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                              <p>No {tab} findings</p>
                            </div>
                          ) : (
                            <ScrollArea className="max-h-[400px]">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[90px]">Severity</TableHead>
                                    <TableHead>Finding</TableHead>
                                    <TableHead>Package</TableHead>
                                    <TableHead>File</TableHead>
                                    <TableHead>CVE</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {findings.map((f, i) => <FindingRow key={`${tab}-${i}`} finding={f} />)}
                                </TableBody>
                              </Table>
                            </ScrollArea>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </>
          )}
        </>
      )}

      {trendData && trend.length < 2 && (
        <Card>
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
            <h3 className="text-lg font-semibold text-muted-foreground">Not Enough Data</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Run at least 2 scans on <code className="bg-muted px-1 rounded">{selectedTarget}</code> to see trends.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScanTrendAnalysis;
